import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { OrganizationUiSettings, OrganizationUiSettingsUpdate } from '../../../types';
import organizationService from '../../../services/organizationService';
import notify from '@/lib/notify';
import { apiErrorDetail } from '@/lib/knowledgeSharing';
import { useAuthStore } from '@/stores/authStore';
import { useOrgUiSettingsStore } from '@/stores/orgUiSettingsStore';

/**
 * The org's ui_settings for the Customization tab: one load, and saves keyed
 * by the control that triggered them so each control shows its own spinner.
 * Results for the active org go to orgUiSettingsStore so the sidebar, theme
 * and route guards refresh right away.
 */
export function useOrgUiSettings(organizationId: string) {
  const { t } = useTranslation();
  // Same org as the one useLoadOrgUiSettings puts in the store.
  const currentOrgId = useAuthStore((s) => s.user?.organization_id ?? null);
  const setOrgUiSettings = useOrgUiSettingsStore((s) => s.setSettings);

  const [settings, setSettings] = useState<OrganizationUiSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    organizationService
      .getUiSettings(organizationId)
      .then((loaded) => {
        if (!cancelled) setSettings(loaded);
      })
      .catch((error: unknown) => {
        console.error('Error loading organization UI settings:', error);
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const applyResult = (updated: OrganizationUiSettings) => {
    setSettings(updated);
    if (organizationId === currentOrgId) setOrgUiSettings(updated);
  };

  const save = async (
    key: string,
    patch: OrganizationUiSettingsUpdate,
    successKey = 'orgExperience.saved'
  ): Promise<boolean> => {
    setSaving(key);
    try {
      applyResult(await organizationService.updateUiSettings(organizationId, patch));
      notify.success(t(successKey));
      return true;
    } catch (error) {
      console.error('Error updating organization UI settings:', error);
      notify.error(apiErrorDetail(error) ?? t('orgExperience.saveError'));
      return false;
    } finally {
      setSaving(null);
    }
  };

  const applyOlisePreset = async (): Promise<boolean> => {
    setSaving('preset');
    try {
      applyResult(await organizationService.applyOlisePreset(organizationId));
      notify.success(t('orgExperience.preset.applied'));
      return true;
    } catch (error) {
      console.error('Error applying the Olisé preset:', error);
      notify.error(apiErrorDetail(error) ?? t('orgExperience.preset.error'));
      return false;
    } finally {
      setSaving(null);
    }
  };

  return { settings, loading, loadError, saving, save, applyOlisePreset };
}

export type OrgUiSettingsController = ReturnType<typeof useOrgUiSettings>;
